import { createFileRoute } from '@tanstack/react-router'

import { JellyIcon } from '@/components/icons'
import { CommentSheet, MenuCard, MenuSheet, PlanPostCard, QueueRow } from '@/components/klient/plan'
import { AppButton, DayRailSection, EmptyState, SectionLabel } from '@/components/shared'
import { AppLink as Link } from '@/components/shell'
import {
  AUTO_APPROVE_IN,
  BATCH_WEEK,
  COMMENT_TARGET,
  MENU_DAILY,
  MENU_DAYS,
  MENU_DAYS_DAILY,
  MENU_DONE,
  MENU_EMPTY,
  MENU_PHOTO,
  PLAN_DAYS,
  QUEUE,
  SPECIALIST,
} from '@/mocks/klient/plan'

/**
 * `stav` is the week itself — `prvni-tyden` before Bára has a batch, `menu` once
 * this week's polední menu is in, `denni` for a venue whose board is rewritten
 * every morning — and `info` is which sheet sits on top.
 */
const STAV_VALUES = ['prvni-tyden', 'menu', 'denni'] as const
type Stav = (typeof STAV_VALUES)[number]
type PlanSearch = { stav?: Stav; info?: 'pripominka' | 'menu' }

export const Route = createFileRoute('/klient/plan')({
  validateSearch: (search: Record<string, unknown>): PlanSearch => ({
    ...(STAV_VALUES.includes(search.stav as Stav) ? { stav: search.stav as Stav } : {}),
    ...(search.info === 'pripominka' || search.info === 'menu'
      ? { info: search.info as 'pripominka' | 'menu' }
      : {}),
  }),
  head: () => ({ meta: [{ title: 'Publikační plán · Aplikace Bitez' }] }),
  component: KlientPlan,
})

const STAGGER_STEP_MS = 90

function KlientPlan() {
  const { stav, info } = Route.useSearch()
  const empty = stav === 'prvni-tyden'
  const daily = stav === 'denni'
  const menu = daily ? MENU_DAILY : stav === 'menu' ? MENU_DONE : MENU_EMPTY

  const pendingCount = PLAN_DAYS.flatMap((group) => group.posts).filter(
    (post) => post.state === 'pending',
  ).length
  let postIndex = 0

  return (
    <div className={`px-4 pt-6 pb-2 ${empty ? 'flex min-h-full flex-col' : ''}`}>
      {/* The count and the countdown live in the sticky tray next to the button
          that answers them — up here only who made the batch and for which week.
          Empty, the header still carries her face: nothing is waiting on the
          client, someone is working on it. */}
      <header className="flex items-center gap-3.5">
        <Link href="/klient/chat" aria-label={`Napsat ${SPECIALIST.name}`} className="shrink-0">
          <img
            src={SPECIALIST.avatar}
            alt=""
            width={150}
            height={150}
            className="size-13 rounded-full object-cover shadow-sm ring-2 ring-white transition-transform duration-150 active:scale-95"
          />
        </Link>
        <div className="min-w-0">
          <SectionLabel>
            {SPECIALIST.name} {empty ? 'chystá' : 'připravila'}
          </SectionLabel>
          <h1 className="font-display mt-0.5 text-2xl leading-[0.9] uppercase">
            {empty ? 'Váš první týden' : `Týden ${BATCH_WEEK}`}
          </h1>
        </div>
      </header>

      {empty ? (
        <EmptyState
          sticker="clock"
          title="Zatím nic ke schválení"
          note="Bára skládá váš první týden. Až bude co ukázat, dá vám vědět — schvalovat budete po týdenních dávkách, vždycky jedním klikem."
          action={{ label: 'Napsat Báře', href: '/klient/chat' }}
        />
      ) : (
        <>
          {/* The one thing here the client ADDS rather than approves (spec §11),
              so it sits above the batch: everything below is Bára's work coming
              towards them, this goes the other way. */}
          <div className="animate-rise-up mt-5">
            <MenuCard menu={menu} />
          </div>

          <div className="mt-6">
            {PLAN_DAYS.map((group, groupIndex) => (
              <DayRailSection
                key={group.date}
                day={group.day}
                date={group.date}
                last={groupIndex === PLAN_DAYS.length - 1}
              >
                <div className="space-y-4 pb-6">
                  {group.posts.map((post) => (
                    <PlanPostCard
                      key={post.image}
                      post={post}
                      commentHref="/klient/plan?info=pripominka"
                      chatHref="/klient/chat"
                      delay={++postIndex * STAGGER_STEP_MS}
                    />
                  ))}
                </div>
              </DayRailSection>
            ))}
          </div>

          {/* Sticky batch action — always within thumb's reach while reviewing.
              It rides its own floating tray (the chat's island header, mirrored at
              the bottom): stacked flush under a post's own Schválit pill it read as
              a third button in the same list instead of the batch-wide one. */}
          {/* -mt cancels the fade's own height, so the tall fade only exists while
              scrolling — at rest the tray sits a normal gap under the last post. */}
          <div className="sticky bottom-0 -mx-4 -mt-10 bg-gradient-to-t from-cream via-cream/90 to-transparent px-4 pt-10 pb-2">
            <div
              className="animate-slide-up rounded-[1.75rem] bg-white p-2 shadow-lg ring-1 ring-ink/5"
              style={{ animationDelay: '250ms' }}
            >
              <AppButton className="w-full">Schválit vše ({pendingCount})</AppButton>
              <p className="mt-1.5 flex items-center justify-center gap-1.5 pb-0.5 text-center text-[10px] font-bold text-brand">
                <JellyIcon name="clock" className="size-2.5" />
                Za {AUTO_APPROVE_IN} se schválí samy
              </p>
            </div>
          </div>

          <section className="mt-6 pb-6">
            <p className="text-[10px] font-bold tracking-widest text-ink/40 uppercase">
              Dál v řadě
            </p>
            <div className="mt-3 space-y-3">
              {QUEUE.map((post) => (
                <QueueRow key={post.image} post={post} />
              ))}
            </div>
          </section>
        </>
      )}

      {/* Written over the plan, never on a screen of its own: taking the client
          away from the post is how you get "ta cena je špatně" with no idea
          which price (spec §7.8). */}
      {info === 'pripominka' && (
        <CommentSheet
          target={COMMENT_TARGET}
          closeHref="/klient/plan"
          specialist={SPECIALIST.name}
        />
      )}

      {/* What the OCR read back, as a receipt to correct — never applied in
          silence, because a wrong price goes to the client's own profile. */}
      {info === 'menu' && (
        <MenuSheet
          photo={MENU_PHOTO}
          days={daily ? MENU_DAYS_DAILY : MENU_DAYS}
          closeHref={stav ? `/klient/plan?stav=${stav}` : '/klient/plan'}
        />
      )}
    </div>
  )
}
